// Google Analytics 4 tracking utility

/**
 * Get user authentication status for analytics
 * @returns {string} 'authenticated' or 'public'
 */
const getUserStatus = () => {
  // Check if user is logged in by checking for auth token
  const token = localStorage.getItem('therapy_auth_token');
  return token ? 'authenticated' : 'public';
};

/**
 * Track an event to Google Analytics 4
 * Automatically adds user_status (authenticated/public) to all events
 * @param {string} eventName - Name of the event (will be converted to snake_case)
 * @param {object} props - Event properties
 */
export const trackEvent = (eventName, props = {}) => {
  // Ensure we're in a browser environment
  if (typeof window === 'undefined') return;
  
  // Check if gtag is available
  if (window.gtag && typeof window.gtag === 'function') {
    try {
      // Convert eventName to GA4 format (snake_case, lowercase)
      const formattedEventName = eventName
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
      
      // Add user status to all events
      const enrichedProps = {
        event_category: props.category || 'engagement',
        event_label: props.location || 'unknown',
        user_status: getUserStatus(), // 'authenticated' or 'public'
        ...props
      };
      
      // Send event to GA4
      window.gtag('event', formattedEventName, enrichedProps);
      
      console.log('📊 GA4 event tracked:', formattedEventName, enrichedProps);
    } catch (error) {
      console.error('❌ GA4 tracking error:', error);
    }
  } else {
    console.log('ℹ️ GA4 not available, event not tracked:', eventName, props);
  }
};

// Initialize GA4 if Measurement ID is available
export const initGA4 = () => {
  const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
    console.log('ℹ️ GA4 Measurement ID not configured');
    return;
  }
  
  // Only load in production or when explicitly enabled
  const isProduction = window.location.hostname === 'yourtherapist.se' || 
                      window.location.hostname === 'www.yourtherapist.se';
  
  if (!isProduction && process.env.NODE_ENV !== 'development') {
    console.log('ℹ️ GA4 not loaded - not in production environment');
    return;
  }
  
  try {
    // Load GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
    
    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      // GDPR compliance settings
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });
    
    console.log('📊 GA4 initialized successfully');
  } catch (error) {
    console.error('❌ GA4 initialization error:', error);
  }
};

export default { trackEvent, initGA4 };